/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useLayoutEffect, useState } from 'react';

// Client-only SPA navigation history: the page we're on and the one before it.
let currentPathname: string | null = null;
let previousPathname: string | null = null;

/**
 * Records pathname changes. Mount once in the app shell layout.
 *
 * Updates during render (idempotent per pathname) so it is already current by the time any child
 * route renders — including children that mount late, e.g. after their loader data resolves.
 * Skipped on the server, where module state would be shared across requests.
 */
export function useTrackPathname(pathname: string): void {
    if (typeof window === 'undefined') return;
    if (pathname !== currentPathname) {
        previousPathname = currentPathname;
        currentPathname = pathname;
    }
}

/**
 * Returns the pathname the shopper was on before the current page, or `null` on a full page load
 * and during server render (read after mount so hydration always matches the server HTML).
 */
export function usePreviousPathname(): string | null {
    const [value, setValue] = useState<string | null>(null);
    useLayoutEffect(() => {
        setValue(previousPathname);
    }, []);
    return value;
}
